import { format } from 'date-fns'
import {
  Activity,
  BarChart3,
  Brain,
  Calendar,
  Clock,
  Dumbbell,
  Gauge,
  Hash,
  Target,
  TrendingDown,
  TrendingUp,
  Trophy,
  Weight,
  Zap
} from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from 'recharts'
import { useUserSettings } from '../api/hooks/useSettings'
import { formatWeight } from '../lib/unit-utils'
import { exerciseProcessor } from '../services/exerciseProcessor'
import { useUIStore } from '../stores/uiStore'
import type { MuscleGroup, Workout } from '../types'
import { MUSCLE_GROUP_COLORS, MUSCLE_GROUP_DISPLAY_NAMES } from '../utils'
import { Badge } from './ui/badge'
import { ChartContainer, ChartTooltip, ChartTooltipContent } from './ui/chart'
import { Modal, ModalContent, ModalHeader, ModalTitle } from './ui/modal'
import { SecondaryMoversToggle } from './ui/secondary-movers-toggle'
import { Separator } from './ui/separator'
import { Tooltip } from './ui/tooltip'

interface WorkoutDetailModalProps {
  workout: Workout
  isOpen: boolean
  onClose: () => void
  navigationElement?: React.ReactNode
}

export default function WorkoutDetailModal({
  workout,
  isOpen,
  onClose,
  navigationElement
}: WorkoutDetailModalProps) {
  const { includeSecondaryMovers, toggleSecondaryMovers, setActiveModal } = useUIStore()
  const { data: userSettings } = useUserSettings()
  const unitSystem = userSettings?.settings.units || 'metric'

  // Calculate comprehensive workout stats
  const workoutStats = useMemo(() => {
    const duration =
      workout.end_time && workout.start_time
        ? Math.round(
            (new Date(workout.end_time).getTime() - new Date(workout.start_time).getTime()) / 60000
          )
        : null

    let totalSets = 0
    let totalReps = 0
    let totalVolume = 0
    let heaviestWeight = 0
    let highestReps = 0
    let lowestReps = Infinity
    let totalRpe = 0
    let rpeCount = 0
    let highestRpe = 0
    let lowestRpe = Infinity

    workout.exercises?.forEach((exercise) => {
      exercise.sets?.forEach((set) => {
        totalSets++
        if (set.reps) {
          totalReps += set.reps
          highestReps = Math.max(highestReps, set.reps)
          lowestReps = Math.min(lowestReps, set.reps)
        }
        if (set.weight_kg) {
          heaviestWeight = Math.max(heaviestWeight, set.weight_kg)
          if (set.reps) {
            totalVolume += set.weight_kg * set.reps
          }
        }
        if (set.rpe) {
          totalRpe += set.rpe
          rpeCount++
          highestRpe = Math.max(highestRpe, set.rpe)
          lowestRpe = Math.min(lowestRpe, set.rpe)
        }
      })
    })

    const avgRepsPerSet = totalSets > 0 ? (totalReps / totalSets).toFixed(1) : '0'
    const avgVolumePerSet = totalSets > 0 ? Math.round(totalVolume / totalSets) : 0
    const minReps = lowestReps === Infinity ? 0 : lowestReps
    const avgRpe = rpeCount > 0 ? (totalRpe / rpeCount).toFixed(1) : null
    const minRpe = lowestRpe === Infinity ? null : lowestRpe

    return {
      duration,
      totalSets,
      totalReps,
      totalVolume,
      heaviestWeight,
      highestReps,
      minReps,
      avgRepsPerSet,
      avgVolumePerSet,
      exerciseCount: workout.exercises?.length || 0,
      avgRpe,
      highestRpe: highestRpe > 0 ? highestRpe : null,
      minRpe,
      rpeCount
    }
  }, [workout])

  // Calculate muscle group volume distribution
  const { muscleGroupData, chartConfig } = useMemo(() => {
    const muscleGroupSets: Record<MuscleGroup, number> = {
      chest: 0,
      lats: 0,
      upperBack: 0,
      rearDelts: 0,
      lateralDelts: 0,
      frontDelts: 0,
      biceps: 0,
      triceps: 0,
      quads: 0,
      hamstrings: 0,
      glutes: 0,
      adductors: 0,
      calves: 0,
      abs: 0,
      forearms: 0,
      neck: 0
    }

    workout.exercises?.forEach((exercise) => {
      const muscleContributions = exerciseProcessor.getMuscleContributionsFromExercise(
        exercise.title,
        exercise.exercise_template_id,
        includeSecondaryMovers
      )
      const setsCount = exercise.sets?.length || 0

      muscleContributions.forEach((contribution) => {
        muscleGroupSets[contribution.muscleGroup] += setsCount * contribution.weight
      })
    })

    // Create chart config and data
    const config: Record<string, { label: string; color: string }> = {
      sets: {
        label: 'Sets',
        color: 'hsl(var(--primary))'
      }
    }

    const data = Object.entries(muscleGroupSets)
      .filter(([_, sets]) => sets > 0)
      .sort((a, b) => b[1] - a[1]) // Sort by sets descending
      .map(([muscleGroup, sets]) => {
        return {
          muscleGroup: MUSCLE_GROUP_DISPLAY_NAMES[muscleGroup as MuscleGroup],
          sets: sets,
          fill: MUSCLE_GROUP_COLORS[muscleGroup as MuscleGroup]
        }
      })

    return { muscleGroupData: data, chartConfig: config }
  }, [workout, includeSecondaryMovers])

  // Calculate muscle group volume data for modal context
  const muscleGroupVolumeData = useMemo(() => {
    const muscleGroupVolume: Record<MuscleGroup, number> = {
      chest: 0,
      lats: 0,
      upperBack: 0,
      rearDelts: 0,
      lateralDelts: 0,
      frontDelts: 0,
      biceps: 0,
      triceps: 0,
      quads: 0,
      hamstrings: 0,
      glutes: 0,
      adductors: 0,
      calves: 0,
      abs: 0,
      forearms: 0,
      neck: 0
    }

    workout.exercises?.forEach((exercise) => {
      // Use getMuscleContributionsFromExercise which accepts exercise name and template ID
      const muscleContributions = exerciseProcessor.getMuscleContributionsFromExercise(
        exercise.title,
        exercise.exercise_template_id,
        includeSecondaryMovers
      )

      // Calculate total volume for this exercise
      const exerciseVolume =
        exercise.sets?.reduce((sum, set) => {
          return sum + (set.reps || 0) * (set.weight_kg || 0)
        }, 0) || 0

      // Distribute volume across muscle groups based on contribution weights
      muscleContributions.forEach((contribution) => {
        muscleGroupVolume[contribution.muscleGroup] += exerciseVolume * contribution.weight
      })
    })

    return Object.entries(muscleGroupVolume)
      .filter(([_, volume]) => volume > 0)
      .map(([muscleGroup, volume]) => ({
        muscleGroup,
        volume: Math.round(volume)
      }))
  }, [workout, includeSecondaryMovers])

  // Update modal context when modal opens or data changes
  useEffect(() => {
    if (isOpen && workout) {
      // Set the modal context with all the workout details
      setActiveModal('workoutDetail', {
        workout,
        stats: workoutStats,
        muscleGroupData,
        muscleGroupVolumeData,
        includeSecondaryMovers
      })
    }

    // Clear modal context when closing
    return () => {
      if (isOpen) {
        setActiveModal(null, null)
      }
    }
  }, [
    isOpen,
    workout,
    workoutStats,
    muscleGroupData,
    muscleGroupVolumeData,
    includeSecondaryMovers,
    setActiveModal
  ])

  return (
    <Modal open={isOpen} onOpenChange={(open) => !open && onClose()} modalId="workoutDetail">
      <ModalContent className="bg-background h-full w-full gap-0 overflow-hidden p-0 md:max-h-[90vh] md:!max-w-[90vw] md:gap-4 md:p-4 lg:!max-w-7xl">
        <ModalHeader className="px-4 pt-4 md:px-0 md:pt-0">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center justify-between gap-4">
                <ModalTitle className="text-2xl font-bold">
                  {workout.title || 'Workout Details'}
                </ModalTitle>
                {navigationElement}
              </div>
              <div className="text-muted-foreground mt-2 flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  <span>{format(new Date(workout.start_time), 'MMMM d, yyyy')}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  <span>{format(new Date(workout.start_time), 'h:mm a')}</span>
                </div>
                {workoutStats.duration && (
                  <div className="flex items-center gap-1">
                    <span>Duration: {workoutStats.duration} minutes</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </ModalHeader>

        <Separator className="my-4" />

        {/* Responsive Layout - Single column on mobile, two columns on desktop */}
        <div className="grid h-auto -translate-y-3.5 grid-cols-1 gap-0 overflow-y-auto px-0 pt-0 pb-4 md:h-[calc(90vh-180px)] md:translate-y-0 md:grid-cols-2 md:gap-2 md:overflow-hidden md:px-0 md:pt-0 md:pb-0">
          {/* Left Column - Info and Graphs */}
          <div className="space-y-2 pt-0 md:space-y-2 md:pt-0">
            {/* Workout Stats Card */}
            <div className="bg-card border-border rounded-none border border-x-0 p-4 md:rounded-lg md:border-x">
              <div className="mb-3 flex items-center gap-2">
                <Activity className="text-muted-foreground h-4 w-4" />
                <h3 className="font-semibold">Workout Statistics</h3>
              </div>

              {/* Primary Stats */}
              <div className="mb-3 grid grid-cols-4 gap-3">
                <div className="text-center">
                  <p className="text-muted-foreground flex items-center justify-center gap-1 text-xs">
                    <Dumbbell className="h-3 w-3" />
                    Exercises
                  </p>
                  <p className="text-xl font-bold">{workoutStats.exerciseCount}</p>
                </div>
                <div className="text-center">
                  <p className="text-muted-foreground flex items-center justify-center gap-1 text-xs">
                    <Hash className="h-3 w-3" />
                    Total Sets
                  </p>
                  <p className="text-xl font-bold">{workoutStats.totalSets}</p>
                </div>
                <div className="text-center">
                  <p className="text-muted-foreground flex items-center justify-center gap-1 text-xs">
                    <Weight className="h-3 w-3" />
                    Volume
                  </p>
                  <p className="text-xl font-bold">
                    {formatWeight(workoutStats.totalVolume, unitSystem, 0)}
                  </p>
                </div>
                <div className="text-center">
                  <Tooltip
                    content={
                      <div className="w-80 p-2 whitespace-normal">
                        <div className="mb-2 text-sm font-semibold">
                          Rate of Perceived Exertion (RPE)
                        </div>
                        <p className="text-muted-foreground mb-2 text-xs">
                          RPE measures how hard you feel your body is working during exercise on a
                          scale of 1-10.
                        </p>
                        <div className="bg-muted/30 mb-2 space-y-1 rounded-md p-2 text-xs">
                          <div>
                            <strong>1-3:</strong> Very easy, could continue for hours
                          </div>
                          <div>
                            <strong>4-6:</strong> Moderate effort, can still hold conversation
                          </div>
                          <div>
                            <strong>7-8:</strong> Hard work, breathing heavily
                          </div>
                          <div>
                            <strong>9-10:</strong> Maximum effort, cannot continue
                          </div>
                        </div>
                        <p className="text-muted-foreground text-xs">
                          <strong>To enable RPE tracking:</strong> Open Hevy → Profile → Settings →
                          Workouts → RPE Tracking
                        </p>
                      </div>
                    }
                    position="top"
                  >
                    <p className="text-muted-foreground flex cursor-help items-center justify-center gap-1 text-xs">
                      <Brain className="h-3 w-3" />
                      Avg RPE
                    </p>
                  </Tooltip>
                  <p className="text-xl font-bold">{workoutStats.avgRpe || '-'}</p>
                </div>
              </div>

              <Separator className="my-3" />

              {/* Power User Stats */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground flex items-center gap-1 text-xs">
                      <Trophy className="h-3 w-3" />
                      Heaviest Weight
                    </span>
                    <span className="text-sm font-medium">
                      {formatWeight(workoutStats.heaviestWeight, unitSystem)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground flex items-center gap-1 text-xs">
                      <Zap className="h-3 w-3" />
                      Total Reps
                    </span>
                    <span className="text-sm font-medium">{workoutStats.totalReps}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground flex items-center gap-1 text-xs">
                      <TrendingUp className="h-3 w-3" />
                      Avg Reps/Set
                    </span>
                    <span className="text-sm font-medium">{workoutStats.avgRepsPerSet}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground flex items-center gap-1 text-xs">
                      <TrendingDown className="h-3 w-3" />
                      Min Reps
                    </span>
                    <span className="text-sm font-medium">{workoutStats.minReps}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground flex items-center gap-1 text-xs">
                      <Target className="h-3 w-3" />
                      Highest Reps
                    </span>
                    <span className="text-sm font-medium">{workoutStats.highestReps}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground flex items-center gap-1 text-xs">
                      <Gauge className="h-3 w-3" />
                      Avg Volume/Set
                    </span>
                    <span className="text-sm font-medium">
                      {formatWeight(workoutStats.avgVolumePerSet, unitSystem, 0)}
                    </span>
                  </div>
                  {workoutStats.rpeCount > 0 && (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground flex items-center gap-1 text-xs">
                          <Brain className="h-3 w-3" />
                          RPE Range
                        </span>
                        <span className="text-sm font-medium">
                          {workoutStats.minRpe} - {workoutStats.highestRpe}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Muscle Group Volume Distribution Card */}
            {muscleGroupData.length > 0 && (
              <div className="bg-card border-border rounded-none border border-x-0 p-4 pb-0 md:rounded-lg md:border-x">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <BarChart3 className="text-muted-foreground h-4 w-4" />
                    <h3 className="font-semibold">Volume Distribution</h3>
                  </div>
                  <SecondaryMoversToggle
                    enabled={includeSecondaryMovers}
                    onToggle={toggleSecondaryMovers}
                    context="charts"
                    size="sm"
                    showLabel={true}
                  />
                </div>
                <ChartContainer config={chartConfig} className="h-[200px] w-full">
                  <BarChart
                    data={muscleGroupData}
                    margin={{ top: 5, right: 5, left: 0 }}
                    barCategoryGap={6}
                  >
                    <CartesianGrid vertical={false} />
                    <XAxis
                      dataKey="muscleGroup"
                      tickLine={false}
                      tickMargin={8}
                      axisLine={false}
                      angle={0}
                      textAnchor="middle"
                      height={40}
                      tick={{ fontSize: 10 }}
                    />
                    <YAxis hide />
                    <ChartTooltip
                      content={
                        <ChartTooltipContent
                          formatter={(value, _name, props) => {
                            return (
                              <>
                                <div className="flex items-center gap-2">
                                  <div
                                    className="h-3 w-3 rounded-sm"
                                    style={{ backgroundColor: props.payload.fill }}
                                  />
                                  <span className="font-medium">{props.payload.muscleGroup}</span>
                                </div>
                                <span className="ml-auto font-mono font-medium tabular-nums">
                                  {includeSecondaryMovers ? Number(value).toFixed(1) : value} sets
                                </span>
                              </>
                            )
                          }}
                        />
                      }
                    />
                    <Bar dataKey="sets" radius={[4, 4, 0, 0]}>
                      {muscleGroupData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ChartContainer>
              </div>
            )}
          </div>

          {/* Right Column - Exercise Details */}
          <div className="mt-2 space-y-2 pb-4 md:mt-0 md:space-y-2 md:overflow-y-auto md:pb-4">
            {workout.exercises?.map((exercise, exerciseIdx) => {
              const exerciseVolume =
                exercise.sets?.reduce((acc, set) => {
                  if (set.weight_kg && set.reps) {
                    return acc + set.weight_kg * set.reps
                  }
                  return acc
                }, 0) || 0

              const muscleContributions = exerciseProcessor.getMuscleContributionsFromExercise(
                exercise.title,
                exercise.exercise_template_id,
                true
              )

              return (
                <div
                  key={exerciseIdx}
                  className="bg-card border-border space-y-3 rounded-none border border-x-0 p-4 md:rounded-lg md:border-x"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h4 className="text-base font-semibold">{exercise.title}</h4>
                      <div className="mt-1 flex flex-wrap gap-2">
                        {muscleContributions.map((contribution) => (
                          <Badge
                            key={contribution.muscleGroup}
                            variant={contribution.isPrimary ? 'default' : 'outline'}
                            className="text-xs"
                            style={
                              contribution.isPrimary
                                ? {
                                    backgroundColor: MUSCLE_GROUP_COLORS[contribution.muscleGroup],
                                    color: 'white',
                                    borderColor: MUSCLE_GROUP_COLORS[contribution.muscleGroup]
                                  }
                                : {
                                    borderColor: MUSCLE_GROUP_COLORS[contribution.muscleGroup],
                                    color: MUSCLE_GROUP_COLORS[contribution.muscleGroup]
                                  }
                            }
                          >
                            {MUSCLE_GROUP_DISPLAY_NAMES[contribution.muscleGroup]}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{exercise.sets?.length || 0} sets</p>
                      <p className="text-muted-foreground text-xs">
                        {formatWeight(exerciseVolume, unitSystem)} vol
                      </p>
                    </div>
                  </div>

                  {exercise.sets && exercise.sets.length > 0 && (
                    <div className="bg-background/50 rounded-md p-3">
                      <div className="text-muted-foreground mb-2 grid grid-cols-5 gap-2 text-xs font-medium sm:gap-3">
                        <span>Set</span>
                        <span>Weight</span>
                        <span>Reps</span>
                        <span>
                          <Tooltip
                            content={
                              <div className="w-80 p-2 whitespace-normal">
                                <div className="mb-2 text-sm font-semibold">
                                  Rate of Perceived Exertion (RPE)
                                </div>
                                <p className="text-muted-foreground mb-2 text-xs">
                                  RPE measures how hard you feel your body is working during
                                  exercise on a scale of 1-10.
                                </p>
                                <div className="bg-muted/30 mb-2 space-y-1 rounded-md p-2 text-xs">
                                  <div>
                                    <strong>1-3:</strong> Very easy, could continue for hours
                                  </div>
                                  <div>
                                    <strong>4-6:</strong> Moderate effort, can still hold
                                    conversation
                                  </div>
                                  <div>
                                    <strong>7-8:</strong> Hard work, breathing heavily
                                  </div>
                                  <div>
                                    <strong>9-10:</strong> Maximum effort, cannot continue
                                  </div>
                                </div>
                                <p className="text-muted-foreground text-xs">
                                  <strong>To enable RPE tracking:</strong> Open Hevy → Profile →
                                  Settings → Workouts → RPE Tracking
                                </p>
                              </div>
                            }
                            position="top"
                          >
                            <span className="cursor-help">RPE</span>
                          </Tooltip>
                        </span>
                        <span>Volume</span>
                      </div>
                      {exercise.sets.map((set, setIdx) => {
                        const setVolume = (set.weight_kg || 0) * (set.reps || 0)
                        return (
                          <div
                            key={setIdx}
                            className="border-border/50 grid grid-cols-5 gap-2 border-t py-1.5 text-sm sm:gap-3"
                          >
                            <span className="font-medium">{setIdx + 1}</span>
                            <span>
                              {set.weight_kg ? formatWeight(set.weight_kg, unitSystem) : '-'}
                            </span>
                            <span>{set.reps || '-'}</span>
                            <span>{set.rpe || '-'}</span>
                            <span className="text-muted-foreground">
                              {setVolume > 0 ? formatWeight(setVolume, unitSystem) : '-'}
                            </span>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}

            {(!workout.exercises || workout.exercises.length === 0) && (
              <div className="text-muted-foreground py-8 text-center">
                No exercises recorded for this workout
              </div>
            )}
          </div>
        </div>
      </ModalContent>
    </Modal>
  )
}
